import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { COACHING_MANIFESTATION_DISCLAIMER } from '@mlm/shared/coaching';
import { linkButton } from '../components/panels/today';
import { Block, ErrorNote, Loading, Row, Screen } from '../components/primitives';
import { MoreWithPlan } from '../components/pro-lock';
import { SteppedSession } from '../components/stepped-session';
import { coaching, type ManifestationPractice } from '../lib/api';
import { useManifestationPractices } from '../lib/queries';
import { ToolFrame } from './tool-frame';

/**
 * The manifestation toolkit: tapping, the water blessing and quantum jumping.
 * A Manifest practice, so it is free the way the 369 and the vision board are
 * and does not follow the Align tools' Pro knob.
 */
export function ToolkitPage() {
  const library = useManifestationPractices();
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;
  const { practices, proOnlyCount } = library.data;

  return (
    <Screen
      back="/manifest"
      eyebrow="Toolkit"
      title="Three ways to work on it"
      subtitle="Each one is a few minutes, walked a step at a time. Pick whichever you can actually face today."
    >
      {practices.map((p) => (
        <Row
          key={p.id}
          title={p.title}
          meta={`${p.minutes} min · ${p.steps.length} steps`}
          to={`/manifest/toolkit/${encodeURIComponent(p.id)}`}
        />
      ))}
      <MoreWithPlan count={proOnlyCount} />
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        {COACHING_MANIFESTATION_DISCLAIMER}
      </p>
    </Screen>
  );
}

export function ToolkitSessionPage() {
  const { id = '' } = useParams();
  const library = useManifestationPractices();
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;
  const practice = library.data.practices.find((p) => p.id === id);
  if (!practice) {
    return (
      <ToolFrame close="/manifest/toolkit">
        <Block
          title="This practice is not available"
          action={
            <Link to="/manifest/toolkit" className={linkButton}>
              Choose another
            </Link>
          }
        >
          It may have been changed or removed. Choose another practice.
        </Block>
      </ToolFrame>
    );
  }
  return <Session practice={practice} />;
}

function Session({ practice }: { practice: ManifestationPractice }) {
  const fire = useCallback(() => {
    coaching.event('manifestation_completed', {
      practice: practice.id,
      minutes: practice.minutes,
    });
  }, [practice]);

  return (
    <ToolFrame close="/manifest/toolkit">
      <SteppedSession
        eyebrow="Toolkit"
        title={practice.title}
        intro={
          <>
            <p>{practice.description}</p>
            <p className="mt-3 text-sm text-ink/60">{practice.origin}</p>
          </>
        }
        meta={`${practice.minutes} min · ${practice.steps.length} steps`}
        steps={practice.steps}
        practice="manifestation"
        event={fire}
        backTo="/manifest/toolkit"
        disclaimer={COACHING_MANIFESTATION_DISCLAIMER}
      />
    </ToolFrame>
  );
}
